import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { HelpCircle, ChevronDown, ChevronUp, Hammer, Building2, ArrowLeft } from 'lucide-react';
import axios from 'axios';

import { BACKEND_URL } from '../config/api';

export default function FAQ() {
  const navigate = useNavigate();
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('tradesperson');
  const [expandedIds, setExpandedIds] = useState(new Set());

  useEffect(() => {
    fetchFAQs();
  }, []);

  const fetchFAQs = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/faqs`);
      setFaqs(response.data);
    } catch (error) {
      console.error('Failed to fetch FAQs:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleExpanded = (faqId) => {
    const newExpanded = new Set(expandedIds);
    if (newExpanded.has(faqId)) {
      newExpanded.delete(faqId);
    } else {
      newExpanded.add(faqId);
    }
    setExpandedIds(newExpanded);
  };

  const filteredFaqs = faqs.filter(faq => faq.category === activeTab);

  const tradespersonFaqs = faqs.filter(faq => faq.category === 'tradesperson');
  const employerFaqs = faqs.filter(faq => faq.category === 'employer');

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
            data-testid="back-home-btn"
          >
            <ArrowLeft className="h-5 w-5" />
            <span className="font-medium">Back to Home</span>
          </button>
          <div className="flex items-center gap-2">
            <HelpCircle className="h-6 w-6 text-orange-500" />
            <span className="font-bold text-slate-900">FAQ</span>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4" data-testid="faq-title">
            Frequently Asked Questions
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Find answers to common questions about using OnsiteHand. Select your category below.
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="flex justify-center gap-4 mb-10">
          <button
            onClick={() => setActiveTab('tradesperson')}
            className={`flex items-center gap-2 px-6 py-3 rounded-full font-medium transition-all ${
              activeTab === 'tradesperson'
                ? 'bg-orange-500 text-white shadow-lg shadow-orange-500/30'
                : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
            }`}
            data-testid="tab-tradesperson"
          >
            <Hammer className="h-5 w-5" />
            For Tradespeople
            <span className={`text-xs px-2 py-0.5 rounded-full ${
              activeTab === 'tradesperson' ? 'bg-white/20' : 'bg-slate-100'
            }`}>
              {tradespersonFaqs.length}
            </span>
          </button>
          <button
            onClick={() => setActiveTab('employer')}
            className={`flex items-center gap-2 px-6 py-3 rounded-full font-medium transition-all ${
              activeTab === 'employer'
                ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/30'
                : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
            }`}
            data-testid="tab-employer"
          >
            <Building2 className="h-5 w-5" />
            For Employers
            <span className={`text-xs px-2 py-0.5 rounded-full ${
              activeTab === 'employer' ? 'bg-white/20' : 'bg-slate-100'
            }`}>
              {employerFaqs.length}
            </span>
          </button>
        </div>

        {/* FAQ List */}
        <div className="space-y-4" data-testid="faq-list">
          {filteredFaqs.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-2xl border border-slate-200">
              <HelpCircle className="h-16 w-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 text-lg">No FAQs available for this category yet.</p>
            </div>
          ) : (
            filteredFaqs.map((faq) => (
              <div
                key={faq.faq_id}
                className="bg-white rounded-xl border border-slate-200 overflow-hidden transition-shadow hover:shadow-md"
                data-testid={`faq-item-${faq.faq_id}`}
              >
                <button
                  onClick={() => toggleExpanded(faq.faq_id)}
                  className="w-full flex items-center justify-between p-5 text-left"
                  data-testid={`faq-toggle-${faq.faq_id}`}
                >
                  <span className="font-semibold text-slate-900 pr-4">{faq.question}</span>
                  {expandedIds.has(faq.faq_id) ? (
                    <ChevronUp className={`h-5 w-5 flex-shrink-0 ${
                      activeTab === 'tradesperson' ? 'text-orange-500' : 'text-blue-500'
                    }`} />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-slate-400 flex-shrink-0" />
                  )}
                </button>
                {expandedIds.has(faq.faq_id) && (
                  <div className={`px-5 pb-5 pt-0 border-t ${
                    activeTab === 'tradesperson' ? 'border-orange-100 bg-orange-50/30' : 'border-blue-100 bg-blue-50/30'
                  }`}>
                    <p className="text-slate-600 leading-relaxed whitespace-pre-wrap">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* Contact Section */}
        <div className="mt-16 text-center">
          <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-2xl p-8 text-white">
            <h2 className="text-2xl font-bold mb-2">Still have questions?</h2>
            <p className="text-orange-100 mb-6">
              Can't find what you're looking for? We're here to help.
            </p>
            <a
              href="mailto:support@onsitehand.com"
              className="inline-block bg-white text-orange-600 font-semibold px-6 py-3 rounded-full hover:bg-orange-50 transition-colors"
              data-testid="contact-support-btn"
            >
              Contact Support
            </a>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-8 px-4 mt-16">
        <div className="max-w-5xl mx-auto text-center">
          <p className="text-slate-400 text-sm">© 2026 OnsiteHand. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
